// REMVAR — Indicações: quem indica registra o ATO, não o negócio.
//
// Tela leve, pensada pra quem NÃO usa CRM (engenheiro, coordenador, topógrafo).
// Registrar leva 30 segundos e não exige conhecer o Pipeline. Quando o negócio
// entrar, o comercial recebe a SUGESTÃO de creditar a originação a quem indicou
// — o casamento nunca é automático.
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert, AutoComplete, Button, Card, Empty, Form, Input, Modal, Space, Spin,
  Switch, Table, Tag, Tooltip, Typography, message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { API_URL } from '../config';
import { getToken } from '../utils/auth';

const { Title, Text } = Typography;

const STATUS_TAG: Record<string, { cor: string; rotulo: string; dica: string }> = {
  aberta:     { cor: 'blue',    rotulo: 'Aguardando negócio', dica: 'Registrada. Quando a empresa virar negócio no Pipeline, o comercial recebe a sugestão de creditar você.' },
  vinculada:  { cor: 'green',   rotulo: 'Virou negócio',      dica: 'A originação foi creditada a você neste negócio.' },
  expirada:   { cor: 'default', rotulo: 'Expirada',           dica: 'Passou da janela sem virar negócio. Indicação não reserva cliente.' },
  descartada: { cor: 'red',     rotulo: 'Arquivada',          dica: 'O comercial arquivou — veja o motivo.' },
};

const fmtData = (v?: string | null) => (v ? String(v).slice(0, 10).split('-').reverse().join('/') : '—');

export default function ComercialIndicacoes() {
  const [dados, setDados] = useState<any>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [verTodas, setVerTodas] = useState(false);
  const [modalAberto, setModalAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [clientes, setClientes] = useState<Array<{ id: number; razao_social: string }>>([]);
  const [form] = Form.useForm();

  const carregar = useCallback(async () => {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'com_listar_indicacoes', todas: verTodas },
        { headers: { Authorization: token } });
      setDados(r.data.body ? JSON.parse(r.data.body) : r.data);
      setErro(null);
    } catch (e: any) { setErro(e.response?.data?.error || e.message); }
  }, [verTodas]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/comercial/clientes`, { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        setClientes((Array.isArray(d) ? d : []).map((c: any) => ({ id: c.id, razao_social: c.razao_social })));
      } catch { /* o campo de empresa aceita texto livre — a lista é só conveniência */ }
    })();
  }, []);

  async function salvar() {
    const v = await form.validateFields();
    setSalvando(true);
    try {
      const token = await getToken();
      // O campo é texto livre. Se o que foi digitado bate EXATO com um cliente
      // da base, amarra o id (casamento forte na sugestão); senão vai só o nome
      // e o casamento será por similaridade quando o negócio nascer.
      const digitado = String(v.empresa || '').trim();
      const cli = clientes.find(c =>
        c.razao_social.toLowerCase() === digitado.toLowerCase());
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, {
        action: 'com_criar_indicacao',
        empresa_nome: cli ? cli.razao_social : digitado,
        cliente_id: cli ? cli.id : null,
        assunto: v.assunto || null,
        contato_nome: v.contato_nome || null,
        contato_info: v.contato_info || null,
        observacao: v.observacao || null,
      }, { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      message.success(d.ja_existia
        ? 'Você já tinha uma indicação aberta pra essa empresa — mantivemos a original.'
        : 'Indicação registrada. O comercial foi avisado.');
      setModalAberto(false); form.resetFields(); carregar();
    } catch (e: any) {
      message.error(e.response?.data?.error || e.message, 6);
    } finally { setSalvando(false); }
  }

  async function descartar(id: number) {
    let motivo = '';
    Modal.confirm({
      title: 'Arquivar indicação',
      content: (
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Quem indicou vai receber o motivo. Use quando a indicação não procede
            (empresa já era negócio em andamento, contato não existiu, etc).
          </Text>
          <Input.TextArea rows={3} style={{ marginTop: 8 }} placeholder="Motivo"
            onChange={e => { motivo = e.target.value; }} />
        </div>
      ),
      okText: 'Arquivar', cancelText: 'Cancelar',
      onOk: async () => {
        if (!motivo.trim()) { message.warning('Escreva o motivo'); throw new Error('sem motivo'); }
        const token = await getToken();
        await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'com_descartar_indicacao', indicacao_id: id, motivo },
          { headers: { Authorization: token } });
        message.success('Indicação arquivada.');
        carregar();
      },
    });
  }

  if (erro) return <Alert type="error" showIcon message={erro} style={{ margin: 16 }} />;
  if (!dados) return <Spin size="large" style={{ display: 'block', margin: '80px auto' }} />;

  const lista = dados.indicacoes || [];
  const colunas: any[] = [
    { title: 'Empresa', key: 'e', render: (_: any, i: any) => (
        <div>
          <Text strong>{i.cliente_razao_social || i.empresa_nome}</Text>
          {!i.cliente_id && (
            <Tooltip title="Empresa ainda não cadastrada no CRM — o casamento será por nome">
              <Tag style={{ marginLeft: 6, fontSize: 9 }}>fora do CRM</Tag>
            </Tooltip>
          )}
          {i.assunto && <div><Text type="secondary" style={{ fontSize: 11 }}>{i.assunto}</Text></div>}
        </div>) },
    ...(dados.ver_todas ? [{ title: 'Quem indicou', dataIndex: 'indicador_email', width: 230,
        render: (v: string) => <Text style={{ fontSize: 12 }}>{v}</Text> }] : []),
    { title: 'Contato', key: 'c', width: 170, render: (_: any, i: any) =>
        i.contato_nome
          ? <span><Text style={{ fontSize: 12 }}>{i.contato_nome}</Text>
              {i.contato_info && <div><Text type="secondary" style={{ fontSize: 11 }}>{i.contato_info}</Text></div>}</span>
          : <Text type="secondary">—</Text> },
    { title: 'Registrada', dataIndex: 'criado_em', width: 100, render: (v: string) => fmtData(v) },
    { title: 'Válida até', dataIndex: 'expira_em', width: 100, render: (v: string) => fmtData(v) },
    { title: 'Situação', key: 's', width: 170, render: (_: any, i: any) => {
        const t = STATUS_TAG[i.status] || { cor: 'default', rotulo: i.status, dica: '' };
        return (
          <Tooltip title={i.status === 'descartada' && i.descartada_motivo ? i.descartada_motivo : t.dica}>
            <Tag color={t.cor}>{t.rotulo}</Tag>
            {i.negocio_titulo && (
              <div><Text type="secondary" style={{ fontSize: 10 }}>{String(i.negocio_titulo).slice(0, 34)}</Text></div>
            )}
          </Tooltip>);
      } },
    ...(dados.pode_gerenciar ? [{ title: '', key: 'a', width: 90,
        render: (_: any, i: any) => i.status === 'aberta'
          ? <Button size="small" onClick={() => descartar(i.id)}>Arquivar</Button>
          : null }] : []),
  ];

  return (
    <div style={{ padding: 16, maxWidth: 1100 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 4 }}>
        <Title level={4} style={{ margin: 0 }}>Indicações</Title>
        <Space>
          {dados.pode_gerenciar && (
            <>
              <Text type="secondary" style={{ fontSize: 12 }}>Ver de todos</Text>
              <Switch size="small" checked={verTodas} onChange={setVerTodas} />
            </>
          )}
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalAberto(true)}>
            Indiquei a Hydrodata
          </Button>
        </Space>
      </Space>

      <Alert type="info" showIcon style={{ margin: '10px 0' }}
        message="Indicou alguém pra Hydrodata? Registre aqui — leva 30 segundos."
        description="Se você conversou com uma empresa e mandou procurarem a gente, registre antes de o negócio chegar. Quando ele entrar no Pipeline, o comercial recebe a sugestão de creditar a originação a você — que vale 30% da remuneração variável daquele contrato. Sem registro, não há como provar depois quem abriu a porta." />

      <Card size="small">
        {lista.length === 0
          ? <Empty description="Nenhuma indicação registrada ainda" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          : <Table size="small" rowKey="id" columns={colunas} dataSource={lista}
              pagination={lista.length > 20 ? { pageSize: 20 } : false} />}
      </Card>

      <Modal open={modalAberto} onCancel={() => setModalAberto(false)} onOk={salvar}
        confirmLoading={salvando} title="Registrar indicação"
        okText="Registrar" cancelText="Cancelar" destroyOnClose width={560}>
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          {/* AutoComplete e não Select: a empresa indicada normalmente AINDA NÃO
              existe na base (é lead novo). Digitar tem que ser o caminho natural;
              a lista é só atalho pra quem já é cliente. */}
          <Form.Item name="empresa" label="Empresa que você indicou"
            rules={[{ required: true, message: 'Diga qual é a empresa' }]}
            extra="Se ela ainda não estiver na nossa base, é só digitar o nome.">
            <AutoComplete allowClear placeholder="Buscar ou digitar o nome"
              options={clientes.map(c => ({ value: c.razao_social }))}
              filterOption={(i, o) =>
                String(o?.value || '').toLowerCase().includes((i || '').toLowerCase())} />
          </Form.Item>
          <Form.Item name="assunto" label="O que você indicou"
            extra="Ex.: estudo hidrológico pro aterro; batimetria do reservatório.">
            <Input placeholder="Assunto da oportunidade" maxLength={300} />
          </Form.Item>
          <Space style={{ width: '100%' }} size={8}>
            <Form.Item name="contato_nome" label="Contato (opcional)" style={{ flex: 1, minWidth: 200 }}>
              <Input placeholder="Nome de quem vai procurar a gente" />
            </Form.Item>
            <Form.Item name="contato_info" label="Telefone/e-mail (opcional)" style={{ flex: 1, minWidth: 200 }}>
              <Input placeholder="Como falar com ele" />
            </Form.Item>
          </Space>
          <Form.Item name="observacao" label="Observação (opcional)">
            <Input.TextArea rows={2} placeholder="Qualquer contexto que ajude o comercial" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
